import { useState, useEffect, useMemo } from "react";
import type { SeatSlice } from "@b2bportal/air-booking-api";
import type { Person } from "@b2bportal/air-disruption-api";
import type { Airport } from "@b2bportal/air-shopping-api";
import { SeatSelectors, FlightPassengerSelectorsV2 } from "@checkout/states";
import { useCheckoutStateSelector as useSelector } from "@hopper-b2b/checkout";
import { useI18nContext, I18nMarkup } from "@hopper-b2b/i18n";
import type {
  GordianSeatSegment,
  SelectedSeatsSegment,
  TripDetails,
} from "@hopper-b2b/types";
import { getSelectedSeatsSegments } from "@hopper-b2b/utilities";
import { FlightSliceRow } from "../../../SeatSelection/components";
import styles from "./styles.module.scss";

interface SeatsSummaryPanelProps {
  selectedSeatSegments: GordianSeatSegment[];
  tripDetails: TripDetails;
  seatedPassengers: Person[];
  onEditSeatSelectionClick: (idx: number) => void;
  addSeats: () => void;
  airports: { [key: string]: Airport };
  seatSlices: Array<SeatSlice>;
}

export const SeatSummaryPanel = (props: SeatsSummaryPanelProps) => {
  const {
    selectedSeatSegments,
    tripDetails,
    seatedPassengers,
    onEditSeatSelectionClick,
    addSeats,
    airports,
    seatSlices,
  } = props;
  const { t, formatFiatCurrency } = useI18nContext();

  const [outboundSegmentsSeats, setOutboundSegmentsSeats] = useState<
    SelectedSeatsSegment[]
  >([]);
  const [returnSegmentsSeats, setReturnSegmentsSeats] = useState<
    SelectedSeatsSegment[]
  >([]);

  const totalSeatPricing = useSelector(SeatSelectors.getSeatTotalFiatPricing);

  const passengers = useSelector(
    FlightPassengerSelectorsV2.getAllSelectedUserPassengersParent
  );

  useEffect(() => {
    if (selectedSeatSegments.length > 0) {
      const outboundSegmentSelectedSeats = getSelectedSeatsSegments(
        selectedSeatSegments,
        tripDetails.slices[0],
        seatedPassengers,
        tripDetails.slices[0].segmentDetails.length,
        seatSlices[0]
      );
      setOutboundSegmentsSeats(outboundSegmentSelectedSeats);
      if (tripDetails.slices.length > 1) {
        const returnSegmentSelectedSeats = getSelectedSeatsSegments(
          selectedSeatSegments,
          tripDetails.slices[1],
          seatedPassengers,
          tripDetails.slices[0].segmentDetails.length,
          seatSlices[1]
        );
        setReturnSegmentsSeats(returnSegmentSelectedSeats);
      }
    }
  }, [seatSlices, seatedPassengers, selectedSeatSegments, tripDetails.slices]);

  const formattedTotalSeatPricing = useMemo(() => {
    return formatFiatCurrency(totalSeatPricing);
  }, [formatFiatCurrency, totalSeatPricing]);

  return (
    <div className={styles.SeatSummaryPanel}>
      <p className="seats-title">{t("Seats")}</p>
      {selectedSeatSegments.length === 0 ? (
        <>
          <p className="description">{t("emptySeatsDescription")}</p>
          <div className="seats-summary-row-add">
            <button className="add-seats" onClick={addSeats}>
              {t("addSeats")}
            </button>
          </div>
        </>
      ) : (
        <>
          {formattedTotalSeatPricing && passengers.length > 0 ? (
            <p className="description">
              <I18nMarkup
                tKey={"seats.totalSeatPricing"}
                replacements={{
                  count: passengers.length,
                  price: formattedTotalSeatPricing,
                }}
              />
            </p>
          ) : null}
          {seatSlices && seatSlices.length > 0 && seatSlices[0] && (
            <FlightSliceRow
              slice={tripDetails.slices[0]}
              seatSlice={seatSlices[0]}
              airports={airports}
              selectedSeatSegments={selectedSeatSegments}
              passengers={passengers}
              onEdit={onEditSeatSelectionClick}
            />
          )}
          {tripDetails.slices.length > 1 && (
            <>
              {seatSlices.length > 1 && seatSlices[1] ? (
                // Case 1: Separate seat slice for return flight
                <FlightSliceRow
                  slice={tripDetails.slices[1]}
                  seatSlice={seatSlices[1]}
                  airports={airports}
                  selectedSeatSegments={selectedSeatSegments}
                  passengers={passengers}
                  onEdit={onEditSeatSelectionClick}
                />
              ) : seatSlices[0] &&
                seatSlices[0].segments?.length >
                  tripDetails.slices[0].segmentDetails.length ? (
                // Case 2: Single seat slice contains all segments
                <FlightSliceRow
                  slice={tripDetails.slices[1]}
                  seatSlice={seatSlices[0]}
                  airports={airports}
                  selectedSeatSegments={selectedSeatSegments}
                  passengers={passengers}
                  onEdit={onEditSeatSelectionClick}
                />
              ) : null}
            </>
          )}
        </>
      )}
      {/* Can use SeatsSummaryRow as inspo but shouldn't be using from Uber */}
      {/* <SeatsSummaryRow
        outboundSeatSegments={outboundSegmentsSeats}
        returnSeatSegments={returnSegmentsSeats}
        onEditClick={onEditSeatSelectionClick}
        outboundOriginCode={tripDetails.slices[0].originCode}
        returnOriginCode={
          tripDetails.slices.length > 1
            ? tripDetails.slices[1].originCode
            : undefined
        }
        airports={airports}
        description={description}
        addSeats={addSeats}
      /> */}
    </div>
  );
};
